import {
    type AllowedCombinationKey,
    type Combination,
    type MassCode,
    type Product,
    ProductImageSubType,
    type ProductPropertyKey,
    type PropertyConfigCode,
    type SmartNumberMapping,
} from "@/features/sub-families/types";
import { getCombinationKeys } from "@/features/sub-families/utils/change-filter-option.ts";
import { getProductScenario } from "@/features/sub-families/utils/power-supply-dimming-type.ts";
import type { Nullable, Selection } from "@/types";
import { SmartNumberDescriptor, type SmartNumberDescriptorKey } from "@/types";

export const MAPPING_TO_PRODUCT_PROPERTY_KEY_MAP: Partial<Record<SmartNumberDescriptorKey, ProductPropertyKey>> = {
    [SmartNumberDescriptor.BeamAngle]: "beam_angle",
    [SmartNumberDescriptor.CCT]: "cct",
    [SmartNumberDescriptor.CRI]: "cri",
    [SmartNumberDescriptor.DimType]: "dimming_type",
    [SmartNumberDescriptor.LEDChip]: "ledchip",
    [SmartNumberDescriptor.LightDistributor]: "light_distributor",
    [SmartNumberDescriptor.Dimension]: "dimension",
    [SmartNumberDescriptor.PowerSupply]: "supply",
} as const;

// Keep separate as we should be able to override this
export function pickCombinationConfigCodes(
    product: Nullable<Product>,
    massCode: Nullable<MassCode>,
    smartNumberMapping: Nullable<SmartNumberMapping>,
): Selection {
    const productScenario = getProductScenario(product);
    const combinationKeys = getCombinationKeys(product);

    return {
        ...combinationKeys.reduce(
            (acc, key) => {
                const property = key as AllowedCombinationKey;

                let configCode: Nullable<PropertyConfigCode>;
                switch (property) {
                    case "supply": {
                        if (productScenario !== 2) {
                            configCode = null;
                            break;
                        }

                        const currentSupply = smartNumberMapping?.[SmartNumberDescriptor.PowerSupply] ?? null;
                        const supplies = product?.properties?.supply ?? [];

                        configCode = supplies.some((supply) => supply.code === currentSupply) ? currentSupply : null;
                        break;
                    }

                    default: {
                        configCode = product?.properties?.[property]?.configCode ?? null;
                        break;
                    }
                }

                return { ...acc, [key]: configCode };
            },
            {} as Record<AllowedCombinationKey, Nullable<PropertyConfigCode>>,
        ),
        massCode,
    };
}

export function getCombinationFromProduct(product: Product, smartNumberMapping: SmartNumberMapping): Combination {
    return pickCombinationConfigCodes(product, product.massCode, smartNumberMapping) as Combination;
}

export const getProductPropertyByDescriptor = (descriptor: SmartNumberDescriptorKey) => {
    return MAPPING_TO_PRODUCT_PROPERTY_KEY_MAP[descriptor] ?? null;
};

export const getProductTechnicalDrawing = (product: Product) => {
    const images = product.properties.images.value ?? [];
    return images.find((image) => image.subType === ProductImageSubType.Drawing)?.url ?? null;
};

export const getSupplies = (product: Nullable<Product>) => {
    return product?.properties.supply ?? [];
};
